/* eslint-disable react-hooks/set-state-in-effect */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCw, Link2, CheckCircle2, AlertTriangle, ExternalLink, Mail, FileSignature, Plug, Send, Unplug, Inbox, PlayCircle, CalendarClock } from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError, useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { relativeTime, formatDateTime } from "@/lib/constants";

export default function FirmIntegrations() {
    const { user } = useAuth();
    const [status, setStatus] = useState(null);
    const [runs, setRuns] = useState([]);
    const [busy, setBusy] = useState(false);

    async function loadAll() {
        try {
            const [s, r] = await Promise.all([
                api.get("/integrations/practice-manager/status"),
                api.get("/integrations/practice-manager/runs"),
            ]);
            setStatus(s.data);
            setRuns(r.data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    useEffect(() => { loadAll(); }, []);

    async function runSync() {
        setBusy(true);
        try {
            const kickoff = await api.post("/integrations/practice-manager/sync");
            if (kickoff.data?.already_running) {
                toast.info("A sync is already running — watching progress…");
            } else {
                toast.info("Practice Manager sync started in the background — this can take up to 2 minutes.");
            }
            // Poll until the top run is no longer 'running' (or 90s cap)
            const deadline = Date.now() + 120_000;
            let finalRun = null;
            while (Date.now() < deadline) {
                await new Promise((r) => setTimeout(r, 3000));
                try {
                    const { data: rows } = await api.get("/integrations/practice-manager/runs");
                    const top = (rows || [])[0];
                    if (top && top.status && top.status !== "running") {
                        finalRun = top;
                        break;
                    }
                } catch { /* keep polling — transient network is OK */ }
            }
            await loadAll();
            if (!finalRun) {
                toast.warning("Sync is still running — refresh the page in a minute to see the outcome.");
            } else if (finalRun.status === "failed") {
                toast.error(`Sync failed: ${(finalRun.error || "unknown error").slice(0, 200)}`);
            } else {
                const s = finalRun.stats || {};
                const summary = [
                    s.clients_created ? `${s.clients_created} clients created` : null,
                    s.clients_updated ? `${s.clients_updated} clients updated` : null,
                    s.requests_created ? `${s.requests_created} requests created` : null,
                    s.requests_updated ? `${s.requests_updated} requests updated` : null,
                ].filter(Boolean).join(" · ");
                toast.success(summary || "Sync complete");
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    const isAdmin = user?.role === "firm_admin";
    const lastRun = status?.last_run;

    return (
        <div>
            <PageHeader
                eyebrow="Integrations"
                title="Connected systems"
                description="ClientOps pulls clients and jobs from your existing systems so you don't double-enter data. We sit in front of them, not on top."
                testId="integrations-header"
            />

            <AutoSyncBanner />

            {/* Practice Manager card */}
            <div className="co-card overflow-hidden">
                <div className="px-6 py-5 flex items-start gap-4 border-b border-[#E4E4E7]">
                    <div className="grid h-12 w-12 place-items-center rounded-lg bg-[#1a5f2a] text-white shrink-0">
                        <Link2 className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-display text-xl font-medium" data-testid="pm-card-title">NZTaxation Practice Manager</h3>
                            <StatusBadge
                                status={status?.connected ? "open" : "cancelled"}
                                tone={status?.connected ? "green" : "grey"}
                                label={status?.connected ? "Connected" : "Not connected"}
                                testId="pm-connected-badge"
                            />
                        </div>
                        <p className="text-sm text-[#5E636E] mt-1">
                            Job &amp; client manager hosted alongside ClientOps. One-way sync: ClientOps mirrors clients and jobs from Practice Manager.
                        </p>
                        {status?.source_url && (
                            <a
                                href={status.source_url}
                                target="_blank"
                                rel="noreferrer"
                                className="mt-2 inline-flex items-center gap-1 text-xs text-[#5E636E] hover:text-[#0F1115] font-mono"
                                data-testid="pm-source-link"
                            >
                                {status.source_url.replace(/^https?:\/\//, "")} <ExternalLink className="h-3 w-3" />
                            </a>
                        )}
                    </div>
                    <Button
                        onClick={runSync}
                        disabled={busy || !isAdmin}
                        className="bg-[#171717] hover:bg-[#2E2E2E] text-white shrink-0"
                        data-testid="pm-sync-now"
                    >
                        {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Syncing…</> : <><RefreshCw className="h-4 w-4" /> Sync now</>}
                    </Button>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-[#E4E4E7]">
                    <Stat label="In Practice Manager" value={status?.pm_live_jobs ?? "—"} sub="live jobs" testId="pm-live-jobs" />
                    <Stat label="In Practice Manager" value={status?.pm_live_gst_clients ?? "—"} sub="GST clients" testId="pm-live-clients" />
                    <Stat label="Imported here" value={status?.imported_clients ?? "—"} sub="clients" testId="pm-imp-clients" />
                    <Stat label="Imported here" value={status?.imported_requests ?? "—"} sub="requests" testId="pm-imp-requests" />
                </div>

                <div className="px-6 py-4 bg-[#FAFAFA] border-t border-[#E4E4E7] text-xs text-[#5E636E]">
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                        <span>
                            Read-only sync. Practice Manager remains the source of truth for jobs.
                            Editing a synced client / request in ClientOps will be overwritten on the next sync.
                            {!isAdmin && " Firm admin only can trigger syncs."}
                        </span>
                    </div>
                </div>
            </div>

            {/* Last run + history */}
            <div className="mt-6 grid gap-4 lg:grid-cols-3">
                <div className="lg:col-span-1 co-card co-card-pad">
                    <div className="co-label">Last sync</div>
                    {!lastRun ? (
                        <p className="mt-3 text-sm text-[#5E636E]">Never run. Hit &ldquo;Sync now&rdquo; to import.</p>
                    ) : (
                        <>
                            <div className="mt-3 flex items-center gap-2">
                                {lastRun.status === "success" ? (
                                    <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                                ) : (
                                    <AlertTriangle className="h-4 w-4 text-rose-600" />
                                )}
                                <span className="text-sm font-medium capitalize">{lastRun.status}</span>
                            </div>
                            <div className="mt-1 text-xs text-[#5E636E]">{relativeTime(lastRun.finished_at || lastRun.started_at)}</div>
                            <dl className="mt-4 grid grid-cols-2 gap-y-1.5 text-xs">
                                <Row k="Clients new" v={lastRun.stats?.clients_created ?? 0} />
                                <Row k="Clients updated" v={lastRun.stats?.clients_updated ?? 0} />
                                <Row k="Entities new" v={lastRun.stats?.entities_created ?? 0} />
                                <Row k="Requests new" v={lastRun.stats?.requests_created ?? 0} />
                                <Row k="Requests updated" v={lastRun.stats?.requests_updated ?? 0} />
                                <Row k="Archived skipped" v={lastRun.stats?.jobs_skipped_archived ?? 0} />
                            </dl>
                            {lastRun.error && (
                                <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                                    {lastRun.error}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="lg:col-span-2 co-card overflow-hidden">
                    <div className="border-b border-[#E4E4E7] px-6 py-3 co-label">Recent runs</div>
                    {runs.length === 0 ? (
                        <div className="p-6 text-sm text-[#5E636E]">No history yet.</div>
                    ) : (
                        <table className="w-full text-sm" data-testid="pm-runs-table">
                            <thead className="bg-[#FAFAFA] text-[11px] uppercase tracking-wider text-[#5E636E]">
                                <tr>
                                    <th className="text-left px-6 py-2">When</th>
                                    <th className="text-left px-6 py-2">By</th>
                                    <th className="text-left px-6 py-2">Status</th>
                                    <th className="text-left px-6 py-2">Created</th>
                                    <th className="text-left px-6 py-2">Updated</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-[#E4E4E7]">
                                {runs.map((r) => (
                                    <tr key={r.id} data-testid={`pm-run-${r.id}`}>
                                        <td className="px-6 py-2 text-xs">{formatDateTime(r.started_at)}</td>
                                        <td className="px-6 py-2 text-xs">{r.actor_name || "—"}</td>
                                        <td className="px-6 py-2 text-xs capitalize">{r.status}</td>
                                        <td className="px-6 py-2 text-xs font-mono">
                                            {(r.stats?.clients_created ?? 0) + (r.stats?.entities_created ?? 0) + (r.stats?.requests_created ?? 0)}
                                        </td>
                                        <td className="px-6 py-2 text-xs font-mono">
                                            {(r.stats?.clients_updated ?? 0) + (r.stats?.requests_updated ?? 0)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Outlook + Jotform integration cards */}
            <OutlookCard isAdmin={isAdmin} />
            <JotformCard isAdmin={isAdmin} />
            <EmailIntakeCard isAdmin={isAdmin} />
            <XeroCard isAdmin={isAdmin} />
            <ProsaicCard isAdmin={isAdmin} />

            {/* Roadmap / placeholders for other connectors */}
            <div className="mt-8">
                <div className="co-label">Coming next</div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                    <PlaceholderCard name="Hubdoc" hint="Forward processed documents to Hubdoc after review." />
                    <PlaceholderCard name="Stripe billing" hint="Charge firms per active client portal seat." />
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value, sub, testId }) {
    return (
        <div className="px-6 py-4" data-testid={testId}>
            <div className="text-[10px] uppercase tracking-wider text-[#5E636E]">{label}</div>
            <div className="font-display text-3xl font-medium mt-1 leading-none">{value}</div>
            <div className="text-xs text-[#5E636E] mt-1">{sub}</div>
        </div>
    );
}

function AutoSyncBanner() {
    const { user } = useAuth();
    const isAdmin = user?.role === "firm_admin";
    const [state, setState] = useState(null);
    const [busy, setBusy] = useState(false);

    async function load() {
        try {
            const { data } = await api.get("/integrations/sync/status");
            setState(data);
        } catch {
            setState({ schedule: "Daily at 09:00 NZ", last_runs: [], has_run_today: false });
        }
    }
    useEffect(() => { load(); }, []);

    async function runNow() {
        if (!isAdmin) return;
        setBusy(true);
        try {
            const kickoff = await api.post("/integrations/sync/run-now");
            if (kickoff.data?.status === "already_running") {
                toast.info("A sync is already running — watching progress…");
            } else {
                toast.info("Xero + Prosaic sync started in the background — this can take a couple of minutes for large firms.");
            }
            // Poll /status until in_progress flips back to false (or 3-minute cap)
            const deadline = Date.now() + 180_000;
            let done = false;
            let latest = null;
            while (Date.now() < deadline) {
                await new Promise((r) => setTimeout(r, 4000));
                try {
                    const { data } = await api.get("/integrations/sync/status");
                    setState(data);
                    if (!data.in_progress) {
                        latest = data.last_runs?.[0];
                        done = true;
                        break;
                    }
                } catch { /* keep polling */ }
            }
            if (!done) {
                toast.warning("Sync is still running — refresh the page in a minute to see the outcome.");
            } else if (latest) {
                const total = (latest.xero_ok || 0) + (latest.prosaic_ok || 0);
                const errs = (latest.xero_errors || 0) + (latest.prosaic_errors || 0);
                if (errs > 0) {
                    toast.warning(`Synced ${total} connections · ${errs} error${errs === 1 ? "" : "s"} — check the connection cards below.`);
                } else {
                    toast.success(`Synced ${total} client data source${total === 1 ? "" : "s"} (Xero + Prosaic)`);
                }
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    const last = state?.last_runs?.[0];
    const lastText = last
        ? `Last run ${relativeTime(last.ran_at)} · ${last.xero_ok || 0} Xero, ${last.prosaic_ok || 0} Prosaic${(last.xero_errors + last.prosaic_errors) > 0 ? ` · ${(last.xero_errors || 0) + (last.prosaic_errors || 0)} error(s)` : ""}`
        : "No runs yet — the first daily sync will fire at 9 AM NZ.";

    return (
        <div className="mt-6 co-card co-card-pad flex flex-wrap items-start gap-4" data-testid="auto-sync-banner">
            <div className="grid h-10 w-10 place-items-center rounded-lg bg-emerald-100 text-emerald-700 shrink-0">
                <CalendarClock className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-[240px]">
                <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-display text-base font-semibold">Daily auto-sync — Xero &amp; Prosaic</h3>
                    <StatusBadge
                        status={state?.has_run_today ? "open" : "pending"}
                        tone={state?.has_run_today ? "green" : "grey"}
                        label={state?.has_run_today ? "Ran today" : "Waiting"}
                        testId="auto-sync-status"
                    />
                </div>
                <p className="text-sm text-[#5E636E] mt-0.5">
                    {state?.schedule || "Daily at 09:00 NZ"} — pulls every mapped Xero + Prosaic connection so client dashboards stay fresh without anyone lifting a finger.
                </p>
                <p className="text-xs text-[#5E636E] mt-1" data-testid="auto-sync-last-run">{lastText}</p>
            </div>
            {isAdmin && (
                <Button
                    variant="outline"
                    onClick={runNow}
                    disabled={busy}
                    className="shrink-0"
                    data-testid="auto-sync-run-now"
                >
                    {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Syncing…</> : <><RefreshCw className="h-4 w-4" /> Sync all now</>}
                </Button>
            )}
        </div>
    );
}

function Row({ k, v }) {
    return (
        <>
            <dt className="text-[#5E636E]">{k}</dt>
            <dd className="font-mono text-right">{v}</dd>
        </>
    );
}

function PlaceholderCard({ name, hint }) {
    return (
        <div className="co-card co-card-pad opacity-70">
            <div className="flex items-center justify-between">
                <div className="font-display text-lg font-medium">{name}</div>
                <span className="rounded-full bg-zinc-100 text-zinc-600 px-2 py-0.5 text-[10px]">Soon</span>
            </div>
            <p className="mt-2 text-xs text-[#5E636E]">{hint}</p>
        </div>
    );
}


function OutlookCard({ isAdmin }) {
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);

    async function load() {
        try {
            const { data } = await api.get("/integrations/outlook/status");
            setStatus(data);
        } catch (e) { /* ignore */ }
    }
    useEffect(() => { load(); }, []);

    async function connect() {
        setBusy(true);
        try {
            // force_consent=1 makes Microsoft show the permissions screen again
            // — required after we add new scopes (like Mail.ReadWrite.Shared)
            // otherwise Azure AD silently reuses the old grant and we can't
            // read the shared mailbox.
            const { data } = await api.get("/integrations/outlook/login", {
                params: { force_consent: 1 },
            });
            window.location.href = data.auth_url;
        } catch (e) {
            toast.error(formatApiError(e));
            setBusy(false);
        }
    }
    async function disconnect() {
        if (!window.confirm("Disconnect Outlook? Future portal-invite emails won't be sent until you reconnect.")) return;
        setBusy(true);
        try {
            await api.post("/integrations/outlook/disconnect");
            toast.success("Disconnected");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    }
    async function sendTest() {
        setBusy(true);
        try {
            const { data } = await api.post("/integrations/outlook/test");
            if (data.status === "sent") {
                toast.success(`Test email sent — check ${status?.connected_user || "your inbox"}`);
            } else {
                toast.info(`Skipped: ${data.reason || "unknown"}`);
            }
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    }

    const connected = status?.status === "connected";
    return (
        <div className="mt-8 co-card overflow-hidden" data-testid="outlook-card">
            <div className="p-6 flex items-start gap-4 border-b border-[#E4E4E7]">
                <div className="h-10 w-10 grid place-items-center rounded-full bg-[#FAFAFA] text-[#171717]">
                    <Mail className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display text-xl font-medium" data-testid="outlook-card-title">Outlook (Microsoft 365)</h3>
                        <StatusBadge
                            status={connected ? "open" : "cancelled"}
                            tone={connected ? "green" : "grey"}
                            label={connected ? "Connected" : "Not connected"}
                            testId="outlook-status-badge"
                        />
                    </div>
                    <p className="text-sm text-[#5E636E] mt-1">
                        Send portal invite emails &amp; notifications directly from your firm&apos;s Outlook mailbox.
                        Replies thread into your Outlook inbox; sent items appear in your Outlook Sent folder.
                    </p>
                    {connected ? (
                        <div className="mt-2 text-xs text-[#5E636E]">
                            Connected as <span className="font-mono text-[#0F1115]" data-testid="outlook-upn">{status?.connected_user}</span>
                            {status?.sent_count > 0 && (
                                <span className="ml-3">· {status.sent_count} email{status.sent_count === 1 ? "" : "s"} sent</span>
                            )}
                        </div>
                    ) : (
                        <div className="mt-2 text-xs text-[#5E636E]">
                            Click <strong>Connect Outlook</strong>, sign in with your firm admin account, and grant <code className="font-mono">Mail.Send</code>.
                        </div>
                    )}
                </div>
                <div className="flex gap-2 shrink-0">
                    {connected ? (
                        <>
                            <Button variant="outline" size="sm" onClick={sendTest} disabled={busy || !isAdmin} data-testid="outlook-test">
                                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Test
                            </Button>
                            <Button variant="outline" size="sm" onClick={disconnect} disabled={busy || !isAdmin} data-testid="outlook-disconnect">
                                <Unplug className="h-3.5 w-3.5" /> Disconnect
                            </Button>
                        </>
                    ) : (
                        <Button onClick={connect} disabled={busy || !isAdmin} className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="outlook-connect">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Connect Outlook
                        </Button>
                    )}
                </div>
            </div>
            {status?.last_error && (
                <div className="px-6 py-3 bg-rose-50 border-t border-rose-200 text-xs text-rose-800">
                    <AlertTriangle className="inline h-3.5 w-3.5 mr-1" /> {status.last_error}
                </div>
            )}
            {connected && (status?.scopes?.length > 0 || status?.missing_scopes?.length > 0) && (
                <OutlookScopes
                    scopes={status?.scopes || []}
                    missing={status?.missing_scopes || []}
                    onReconsent={connect}
                    canReconsent={isAdmin && !busy}
                />
            )}
        </div>
    );
}


function OutlookScopes({ scopes, missing, onReconsent, canReconsent }) {
    const hasMissing = missing.length > 0;
    // Human-friendly labels for the scopes we actually request.
    const LABELS = {
        "mail.send": "Send mail as connected user",
        "mail.read": "Read mail",
        "mail.readwrite": "Read/write mail",
        "mail.read.shared": "Read shared mailboxes",
        "mail.readwrite.shared": "Read/write shared mailboxes",
        "sites.readwrite.all": "SharePoint sites (read/write)",
        "files.readwrite.all": "SharePoint / OneDrive files (read/write)",
        "user.read": "Read your profile",
    };
    const label = (s) => LABELS[s.toLowerCase()] || s;

    return (
        <div className="px-6 py-3 border-t border-[#E4E4E7] bg-[#FAFAFA]" data-testid="outlook-scopes-panel">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                    <div className="co-label">Granted permissions</div>
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid="outlook-granted-scopes">
                        {scopes.length === 0 && (
                            <span className="text-[11px] text-[#5E636E]">No scopes recorded yet — reconnect to capture them.</span>
                        )}
                        {scopes.map((s) => (
                            <span
                                key={s}
                                className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[10px] text-emerald-800 font-mono"
                                title={label(s)}
                                data-testid={`outlook-scope-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                            >
                                <CheckCircle2 className="h-3 w-3" /> {s}
                            </span>
                        ))}
                    </div>
                    {hasMissing && (
                        <>
                            <div className="mt-3 co-label text-rose-700">Missing (blocks some features)</div>
                            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="outlook-missing-scopes">
                                {missing.map((s) => (
                                    <span
                                        key={s}
                                        className="inline-flex items-center gap-1 rounded-full bg-rose-50 border border-rose-200 px-2 py-0.5 text-[10px] text-rose-800 font-mono"
                                        title={`${label(s)} — click Reconnect to grant`}
                                        data-testid={`outlook-missing-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                    >
                                        <AlertTriangle className="h-3 w-3" /> {s}
                                    </span>
                                ))}
                            </div>
                            <p className="mt-2 text-[11px] text-[#5E636E]">
                                Microsoft is caching an older consent that doesn&apos;t cover these scopes. Click
                                <strong> Reconnect &amp; grant </strong>
                                to force a fresh consent screen — the missing permissions will be added to your token.
                            </p>
                        </>
                    )}
                </div>
                {hasMissing && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onReconsent}
                        disabled={!canReconsent}
                        className="shrink-0"
                        data-testid="outlook-reconsent"
                    >
                        <RefreshCw className="h-3.5 w-3.5" /> Reconnect &amp; grant
                    </Button>
                )}
            </div>
        </div>
    );
}


function JotformCard({ isAdmin }) {
    const [status, setStatus] = useState(null);
    const [forms, setForms] = useState([]);
    const [apiKey, setApiKey] = useState("");
    const [busy, setBusy] = useState(false);
    const [showKey, setShowKey] = useState(false);

    async function loadStatus() {
        try {
            const { data } = await api.get("/integrations/jotform/status");
            setStatus(data);
            if (data.status === "connected") {
                try {
                    const f = await api.get("/integrations/jotform/forms");
                    setForms(f.data || []);
                } catch (e) { setForms([]); }
            }
        } catch (e) { /* ignore */ }
    }
    useEffect(() => { loadStatus(); }, []);

    async function connect() {
        if (!apiKey.trim()) {
            toast.error("Paste your Jotform API key");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/integrations/jotform/connect", { api_key: apiKey.trim() });
            toast.success(`Connected as ${data.username}`);
            setApiKey("");
            setShowKey(false);
            await loadStatus();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    }
    async function disconnect() {
        if (!window.confirm("Disconnect Jotform? Existing signed PDFs stay in ClientOps; new submissions won't sync.")) return;
        setBusy(true);
        try {
            await api.post("/integrations/jotform/disconnect");
            toast.success("Disconnected");
            await loadStatus();
            setForms([]);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    }
    async function setDefault(formId) {
        try {
            await api.post("/integrations/jotform/default-form", { form_id: formId || null });
            toast.success(formId ? "Default form saved" : "Default cleared");
            await loadStatus();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    const connected = status?.status === "connected";
    return (
        <div className="mt-6 co-card overflow-hidden" data-testid="jotform-card">
            <div className="p-6 flex items-start gap-4 border-b border-[#E4E4E7]">
                <div className="h-10 w-10 grid place-items-center rounded-full bg-[#FAFAFA] text-[#171717]">
                    <FileSignature className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display text-xl font-medium" data-testid="jotform-card-title">Jotform e-signatures</h3>
                        <StatusBadge
                            status={connected ? "open" : "cancelled"}
                            tone={connected ? "green" : "grey"}
                            label={connected ? "Connected" : "Not connected"}
                            testId="jotform-status-badge"
                        />
                    </div>
                    <p className="text-sm text-[#5E636E] mt-1">
                        Attach a Jotform signature form to any request. Clients sign in their browser; the signed PDF auto-syncs back to ClientOps and a review task is created.
                    </p>
                    {connected && (
                        <div className="mt-2 text-xs text-[#5E636E]">
                            Connected as <span className="font-mono text-[#0F1115]" data-testid="jotform-username">{status?.username}</span>
                            <span className="ml-3">· {forms.length} form{forms.length === 1 ? "" : "s"} available</span>
                        </div>
                    )}
                </div>
                {connected && (
                    <Button variant="outline" size="sm" onClick={disconnect} disabled={busy || !isAdmin} className="shrink-0" data-testid="jotform-disconnect">
                        <Unplug className="h-3.5 w-3.5" /> Disconnect
                    </Button>
                )}
            </div>
            <div className="p-6">
                {!connected ? (
                    <div className="space-y-3">
                        <div className="co-label">Jotform API key</div>
                        <div className="flex gap-2">
                            <Input
                                type={showKey ? "text" : "password"}
                                value={apiKey}
                                onChange={(e) => setApiKey(e.target.value)}
                                placeholder="e.g. e6a0f4b224309b71758dd180ff054157"
                                className="font-mono text-sm"
                                data-testid="jotform-api-key-input"
                            />
                            <Button variant="outline" size="sm" type="button" onClick={() => setShowKey(!showKey)}>
                                {showKey ? "Hide" : "Show"}
                            </Button>
                            <Button onClick={connect} disabled={busy || !isAdmin} className="bg-[#171717] hover:bg-[#2E2E2E] text-white" data-testid="jotform-connect">
                                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plug className="h-4 w-4" />} Connect
                            </Button>
                        </div>
                        <p className="text-[11px] text-[#5E636E]">
                            Get your key from <a className="underline" href="https://www.jotform.com/myaccount/api" target="_blank" rel="noreferrer">Jotform → Settings → API</a>. Create one with <strong>Full Access</strong>.
                        </p>
                    </div>
                ) : (
                    <div>
                        <div className="co-label">Default signature form</div>
                        <p className="text-[11px] text-[#5E636E] mt-1 mb-2">When staff attach a signature to a new request, this form is preselected.</p>
                        <Select value={status?.default_form_id || ""} onValueChange={(v) => setDefault(v || null)}>
                            <SelectTrigger className="max-w-md" data-testid="jotform-default-form">
                                <SelectValue placeholder="Pick a form" />
                            </SelectTrigger>
                            <SelectContent>
                                {forms.map((f) => (
                                    <SelectItem key={f.id} value={f.id} data-testid={`jotform-form-${f.id}`}>
                                        {f.title} <span className="text-[#5E636E] font-mono ml-2 text-[10px]">{f.id}</span>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {status?.default_form_id && (
                            <button
                                className="mt-2 text-[11px] text-[#5E636E] underline"
                                onClick={() => setDefault(null)}
                                data-testid="jotform-clear-default"
                            >
                                Clear default
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}


function EmailIntakeCard({ isAdmin }) {
    const [status, setStatus] = useState(null);
    const [mailbox, setMailbox] = useState("");
    const [busy, setBusy] = useState(false);
    const [running, setRunning] = useState(false);

    async function load() {
        try {
            const { data } = await api.get("/integrations/email-intake/status");
            setStatus(data);
            setMailbox((prev) => {
                if (prev) return prev;                                   // user is mid-typing — don't clobber
                if (data.mailbox) return data.mailbox;                   // already saved on the server
                if (data.upload_domain) return `intake@${data.upload_domain}`; // sensible default so Save is clickable
                return prev;
            });
        } catch (e) { /* ignore */ }
    }
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

    async function saveMailbox(enabled) {
        const mbx = (mailbox || "").trim().toLowerCase();
        if (!mbx || !mbx.includes("@")) {
            toast.error("Enter a full email address, e.g. intake@upload.nztaxation.nz");
            return;
        }
        setBusy(true);
        try {
            await api.post("/integrations/email-intake/configure", {
                mailbox: mbx,
                enabled: enabled ?? status?.enabled ?? true,
            });
            toast.success("Mailbox saved");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    }

    async function toggleEnabled(nextEnabled) {
        setBusy(true);
        try {
            await api.post("/integrations/email-intake/enabled", { enabled: nextEnabled });
            toast.success(nextEnabled ? "Automatic polling ON (every 5 min)" : "Automatic polling paused");
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setBusy(false); }
    }

    async function runNow() {
        setRunning(true);
        try {
            const { data } = await api.post("/integrations/email-intake/run-now");
            const parts = [
                `${data.fetched ?? 0} fetched`,
                `${data.routed ?? 0} matched`,
                `${data.unmatched ?? 0} unmatched`,
                `${data.attachments_saved ?? 0} files saved`,
            ];
            toast.success(parts.join(" · "));
            await load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally { setRunning(false); }
    }

    const configured = !!status?.mailbox;
    const enabled = !!status?.enabled;
    const connected = !!status?.microsoft_connected;
    const badgeLabel = !configured ? "Not configured" : (enabled ? "Auto polling" : "Paused");
    const badgeTone = !configured ? "grey" : (enabled ? "green" : "amber");
    const badgeStatus = !configured ? "cancelled" : (enabled ? "open" : "in_progress");
    const last = status?.last_summary;

    return (
        <div className="mt-6 co-card overflow-hidden" data-testid="email-intake-card">
            <div className="p-6 flex items-start gap-4 border-b border-[#E4E4E7]">
                <div className="h-10 w-10 grid place-items-center rounded-full bg-[#FAFAFA] text-[#171717]">
                    <Inbox className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display text-xl font-medium" data-testid="email-intake-card-title">Email intake</h3>
                        <StatusBadge status={badgeStatus} tone={badgeTone} label={badgeLabel} testId="email-intake-status-badge" />
                    </div>
                    <p className="text-sm text-[#5E636E] mt-1">
                        Clients forward documents to a shared inbox — ClientOps polls it every 5 minutes,
                        matches each email to a client (via <code className="font-mono">co&lt;short&gt;@{status?.upload_domain || "upload.nztaxation.nz"}</code> alias
                        or sender email), and saves attachments straight into the portal. Unmatched emails land in the Unmatched queue.
                    </p>
                    {!connected && (
                        <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                            <AlertTriangle className="inline h-3.5 w-3.5 mr-1" />
                            Microsoft 365 isn&apos;t connected on this tenant. Connect Outlook above so intake can read the shared mailbox.
                        </div>
                    )}
                </div>
                <div className="flex gap-2 shrink-0">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={runNow}
                        disabled={running || !isAdmin || !configured}
                        data-testid="email-intake-run-now"
                    >
                        {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5" />} Run now
                    </Button>
                    {configured && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleEnabled(!enabled)}
                            disabled={busy || !isAdmin}
                            data-testid="email-intake-toggle-enabled"
                        >
                            {enabled ? "Pause" : "Resume"}
                        </Button>
                    )}
                </div>
            </div>

            <div className="p-6 grid gap-6 lg:grid-cols-[1fr_320px]">
                <div>
                    <div className="co-label">Shared mailbox address</div>
                    <div className="mt-2 flex gap-2 max-w-lg">
                        <Input
                            type="email"
                            value={mailbox}
                            onChange={(e) => setMailbox(e.target.value)}
                            placeholder="e.g. intake@upload.yourfirm.com"
                            className="font-mono text-sm"
                            disabled={!isAdmin}
                            data-testid="email-intake-mailbox-input"
                        />
                        <Button
                            onClick={() => saveMailbox()}
                            disabled={busy || !isAdmin || !mailbox.trim()}
                            className="bg-[#171717] hover:bg-[#2E2E2E] text-white"
                            data-testid="email-intake-save-mailbox"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
                        </Button>
                    </div>
                    <p className="text-[11px] text-[#5E636E] mt-2">
                        Set up a Microsoft 365 shared mailbox or catch-all at
                        <span className="font-mono"> @{status?.upload_domain || "upload.nztaxation.nz"} </span>
                        and grant the connected user <code className="font-mono">Mail.ReadWrite.Shared</code>.
                    </p>
                </div>

                <div className="rounded-md border border-[#E4E4E7] bg-[#FAFAFA] p-4">
                    <div className="co-label">Last run</div>
                    {!last && !status?.last_run_at ? (
                        <p className="mt-2 text-xs text-[#5E636E]">Never run yet.</p>
                    ) : (
                        <>
                            <div className="mt-2 text-xs text-[#5E636E]">
                                {status?.last_run_at ? relativeTime(status.last_run_at) : "—"}
                            </div>
                            {last && (
                                <dl className="mt-3 grid grid-cols-2 gap-y-1 text-xs">
                                    <Row k="Fetched" v={last.fetched ?? 0} />
                                    <Row k="Matched" v={last.routed ?? 0} />
                                    <Row k="Unmatched" v={last.unmatched ?? 0} />
                                    <Row k="Files saved" v={last.attachments_saved ?? 0} />
                                    <Row k="Duplicates" v={last.duplicates ?? 0} />
                                    <Row k="Skipped" v={last.skipped_no_attachments ?? 0} />
                                    <Row k="Errors" v={last.errors ?? 0} />
                                </dl>
                            )}
                            {status?.last_error && (
                                <div className="mt-3 rounded border border-rose-200 bg-rose-50 px-2 py-1.5 text-[11px] text-rose-800">
                                    {status.last_error}
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {(status?.recent?.length ?? 0) > 0 && (
                <div className="border-t border-[#E4E4E7]">
                    <div className="px-6 pt-4 co-label">Recent messages</div>
                    <table className="w-full text-sm" data-testid="email-intake-recent-table">
                        <thead className="bg-[#FAFAFA] text-[11px] uppercase tracking-wider text-[#5E636E]">
                            <tr>
                                <th className="text-left px-6 py-2">When</th>
                                <th className="text-left px-6 py-2">From</th>
                                <th className="text-left px-6 py-2">Subject</th>
                                <th className="text-left px-6 py-2">Result</th>
                                <th className="text-left px-6 py-2">Detail</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[#E4E4E7]">
                            {status.recent.slice(0, 12).map((r) => (
                                <tr key={r.message_id} data-testid={`email-intake-row-${r.message_id}`}>
                                    <td className="px-6 py-2 text-xs">{relativeTime(r.at)}</td>
                                    <td className="px-6 py-2 text-xs font-mono">{r.sender || "—"}</td>
                                    <td className="px-6 py-2 text-xs truncate max-w-[280px]">{r.subject || "(no subject)"}</td>
                                    <td className="px-6 py-2 text-xs">
                                        <StatusPill status={r.status} />
                                    </td>
                                    <td className="px-6 py-2 text-[11px] text-[#5E636E]">
                                        {r.status === "matched" && (
                                            <>
                                                {r.client_name || r.client_id?.slice(0, 8)}
                                                {r.attachments_saved ? ` · ${r.attachments_saved} file${r.attachments_saved === 1 ? "" : "s"}` : ""}
                                                {r.duplicates ? ` · ${r.duplicates} dup` : ""}
                                                <span className="ml-1 text-[10px]">({r.match_method || "match"})</span>
                                            </>
                                        )}
                                        {r.status === "unmatched" && `${r.attachments_stashed || 0} stashed`}
                                        {r.status === "error" && (r.error || "").slice(0, 120)}
                                        {r.status?.startsWith("skipped") && "—"}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function StatusPill({ status }) {
    const tone = status === "matched" ? "bg-emerald-100 text-emerald-800"
        : status === "unmatched" ? "bg-amber-100 text-amber-800"
        : status === "error" ? "bg-rose-100 text-rose-800"
        : "bg-zinc-100 text-zinc-700";
    const label = status === "matched" ? "matched"
        : status === "unmatched" ? "unmatched"
        : status === "error" ? "error"
        : "skipped";
    return <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${tone}`}>{label}</span>;
}


// ---------------------------------------------------------------- XeroCard

function XeroCard({ isAdmin }) {
    const [state, setState] = useState({ configured: false, connections: [] });
    const [clients, setClients] = useState([]);
    const [busy, setBusy] = useState(null); // connection.id being acted upon
    const [flash, setFlash] = useState(null);

    async function load() {
        try {
            const { data } = await api.get("/xero/status");
            setState(data);
        } catch (e) { /* ignore — Xero may not be configured yet */ }
    }
    useEffect(() => {
        load();
        api.get("/clients/").then((r) => setClients(r.data)).catch(() => {});
        // Read one-time flash from URL (?xero_ok=1 or ?xero_error=…)
        const p = new URLSearchParams(window.location.search);
        if (p.get("xero_ok")) {
            setFlash({ tone: "ok", text: `Connected ${p.get("xero_ok")} Xero organisation(s).` });
            window.history.replaceState({}, "", window.location.pathname);
        } else if (p.get("xero_error")) {
            setFlash({ tone: "err", text: `Xero error: ${p.get("xero_error")}` });
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, []);

    async function startConnect() {
        try {
            const { data } = await api.get("/xero/connect");
            window.location.href = data.authorize_url;
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function syncNow(id) {
        setBusy(id);
        try {
            const { data } = await api.post(`/xero/connections/${id}/sync`);
            toast.success(`Synced: cash $${data.cash_balance ?? "—"}, revenue $${data.pl_90d?.revenue ?? "—"}`);
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    }

    async function mapClient(id, clientopsClientId) {
        try {
            await api.patch(`/xero/connections/${id}/map`, { clientops_client_id: clientopsClientId });
            toast.success("Mapped to client. Hit Sync now to pull data.");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    async function disconnect(id) {
        if (!window.confirm("Disconnect this Xero organisation? You can reconnect later.")) return;
        try {
            await api.delete(`/xero/connections/${id}`);
            toast.success("Disconnected");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    return (
        <div className="mt-8 co-card overflow-hidden" data-testid="xero-card">
            <div className="p-6 border-b border-[#E4E4E7] bg-white flex items-start justify-between gap-4">
                <div>
                    <div className="co-label">Data source</div>
                    <h3 className="mt-1 font-display text-xl font-medium">Xero (Accounting)</h3>
                    <p className="mt-1 text-sm text-[#5E636E] max-w-2xl">
                        Pulls each client&apos;s bank balance, 90-day P&amp;L and overdue invoices into ClientOps once a day.
                        Feeds the Business Health Score engine directly. Each client owner authorises their own Xero org — one org per ClientOps client.
                    </p>
                </div>
                {isAdmin && state.configured && (
                    <Button
                        onClick={startConnect}
                        className="bg-[#0E3B2E] hover:bg-[#0B2F25] text-white shrink-0"
                        data-testid="xero-connect-btn"
                    >
                        + Connect Xero
                    </Button>
                )}
            </div>

            {!state.configured && (
                <div className="p-6 text-sm text-amber-800 bg-amber-50 border-b border-amber-200">
                    Xero credentials aren&apos;t configured on the backend. Set <code>XERO_CLIENT_ID</code>, <code>XERO_CLIENT_SECRET</code> and <code>XERO_REDIRECT_URI</code> in <code>backend/.env</code>, then restart.
                </div>
            )}

            {flash && (
                <div className={`p-4 text-sm border-b ${flash.tone === "ok" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-rose-50 text-rose-800 border-rose-200"}`} data-testid="xero-flash">
                    {flash.text}
                </div>
            )}

            <div className="divide-y divide-[#E4E4E7]" data-testid="xero-connections">
                {state.connections.length === 0 && state.configured && (
                    <div className="p-6 text-sm text-[#5E636E]">
                        No Xero organisations connected yet. Click <em>Connect Xero</em> to authorise one — perfect for testing, sign in as Xero&apos;s free Demo Company.
                    </div>
                )}
                {state.connections.map((c) => (
                    <div key={c.id} className="p-6 grid gap-3 md:grid-cols-[1fr_auto] items-center" data-testid={`xero-conn-${c.id}`}>
                        <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{c.xero_tenant_name}</div>
                            <div className="text-[11px] text-[#5E636E] font-mono truncate">Xero org · {c.xero_tenant_id}</div>
                            {c.needs_reconnect && (
                                <div className="mt-1 flex items-center gap-2 flex-wrap" data-testid={`xero-reconnect-row-${c.id}`}>
                                    <span className="text-xs text-rose-700">Needs reconnect · {c.last_error || "refresh failed"}</span>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={startConnect}
                                        className="h-6 text-[11px] border-rose-300 text-rose-800 hover:bg-rose-50"
                                        data-testid={`xero-reconnect-btn-${c.id}`}
                                    >
                                        <RefreshCw className="h-3 w-3" /> Reconnect now
                                    </Button>
                                </div>
                            )}
                            {c.last_synced_at && (
                                <div className="mt-1 text-[11px] text-[#5E636E]">Last sync: {relativeTime(c.last_synced_at)}</div>
                            )}
                            {Array.isArray(c.last_sync_errors) && c.last_sync_errors.length > 0 && (
                                <div className="mt-1 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1" data-testid={`xero-sync-errors-${c.id}`}>
                                    <strong>Last sync had {c.last_sync_errors.length} issue{c.last_sync_errors.length === 1 ? "" : "s"}:</strong>{" "}
                                    {c.last_sync_errors.slice(0, 2).join(" · ")}
                                    {c.last_sync_errors.some((e) => /403|scope|forbidden|unauthori/i.test(String(e))) && (
                                        <div className="mt-1">Likely a Xero scope issue — disconnect + reconnect this org to pick up the latest scopes.</div>
                                    )}
                                </div>
                            )}
                            <div className="mt-2 flex items-center gap-2">
                                <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E]">Maps to client</Label>
                                <Select
                                    value={c.clientops_client_id || ""}
                                    onValueChange={(v) => mapClient(c.id, v)}
                                >
                                    <SelectTrigger className="h-8 w-64" data-testid={`xero-map-${c.id}`}>
                                        <SelectValue placeholder="Pick a ClientOps client…" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {clients.map((cl) => (
                                            <SelectItem key={cl.id} value={cl.id}>{cl.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <Button
                                variant="outline"
                                onClick={() => syncNow(c.id)}
                                disabled={!c.clientops_client_id || busy === c.id}
                                data-testid={`xero-sync-${c.id}`}
                            >
                                {busy === c.id ? "Syncing…" : "Sync now"}
                            </Button>
                            {isAdmin && (
                                <Button
                                    variant="outline"
                                    onClick={() => disconnect(c.id)}
                                    className="text-rose-700 hover:bg-rose-50"
                                    data-testid={`xero-disconnect-${c.id}`}
                                >
                                    Disconnect
                                </Button>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------- ProsaicCard

function ProsaicCard({ isAdmin }) {
    const [state, setState] = useState({ configured: false, entities: [], loading: true });
    const [clients, setClients] = useState([]);
    const [q, setQ] = useState("");
    const [busy, setBusy] = useState(null);

    async function load() {
        setState((s) => ({ ...s, loading: true }));
        try {
            const cfg = await api.get("/prosaic/status");
            if (!cfg.data.configured) {
                setState({ configured: false, entities: [], loading: false });
                return;
            }
            const ent = await api.get("/prosaic/entities");
            setState({ configured: true, entities: ent.data.entities || [], loading: false });
        } catch (e) {
            setState({ configured: false, entities: [], loading: false });
        }
    }
    useEffect(() => {
        load();
        api.get("/clients/").then((r) => setClients(r.data)).catch(() => {});
    }, []);

    async function mapEntity(entity, clientopsClientId) {
        try {
            await api.patch(`/prosaic/entities/${entity.prosaic_entity_id}/map`, {
                clientops_client_id: clientopsClientId,
                prosaic_entity_name: entity.prosaic_entity_name,
            });
            toast.success(`Mapped ${entity.prosaic_entity_name}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    async function syncNow(entity) {
        setBusy(entity.prosaic_entity_id);
        try {
            const { data } = await api.post(`/prosaic/entities/${entity.prosaic_entity_id}/sync`);
            const cash = data.cash_balance != null ? `$${data.cash_balance.toLocaleString("en-NZ")}` : "—";
            const rev = data.profit_loss?.revenue != null ? `$${data.profit_loss.revenue.toLocaleString("en-NZ")}` : "—";
            const score = data.regenerate?.overall_score;
            toast.success(`Synced ${entity.prosaic_entity_name}: cash ${cash}, revenue ${rev}${score != null ? `, health ${score}/100` : ""}`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    }

    const filtered = state.entities.filter((e) => {
        const needle = q.trim().toLowerCase();
        if (!needle) return true;
        return (e.prosaic_entity_name || "").toLowerCase().includes(needle);
    });

    return (
        <div className="mt-8 co-card overflow-hidden" data-testid="prosaic-card">
            <div className="p-6 border-b border-[#E4E4E7] bg-white flex items-start justify-between gap-4">
                <div>
                    <div className="co-label">Data source</div>
                    <h3 className="mt-1 font-display text-xl font-medium">Prosaic (AI Accounting)</h3>
                    <p className="mt-1 text-sm text-[#5E636E] max-w-2xl">
                        Pulls each client&apos;s bank balance and P&amp;L from your firm&apos;s Prosaic workspace once a day.
                        Feeds the Business Health Score engine directly. One API key covers every entity in the workspace — no per-client OAuth.
                    </p>
                </div>
                <Button variant="outline" onClick={load} data-testid="prosaic-refresh">Refresh</Button>
            </div>

            {!state.configured && (
                <div className="p-6 text-sm text-amber-800 bg-amber-50 border-b border-amber-200">
                    Prosaic API key not configured. Set <code>PROSAIC_API_KEY</code> in <code>backend/.env</code> and restart.
                </div>
            )}

            {state.configured && (
                <div className="p-3 border-b border-[#E4E4E7]">
                    <div className="relative">
                        <Input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder={`Search ${state.entities.length} Prosaic entities…`}
                            data-testid="prosaic-search"
                        />
                    </div>
                </div>
            )}

            <div className="divide-y divide-[#E4E4E7] max-h-[70vh] overflow-y-auto" data-testid="prosaic-entity-list">
                {state.loading && (
                    <div className="p-6 text-sm text-[#5E636E]"><Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…</div>
                )}
                {!state.loading && state.configured && filtered.length === 0 && (
                    <div className="p-6 text-sm text-[#5E636E]">No entities match.</div>
                )}
                {filtered.map((e) => (
                    <div key={e.prosaic_entity_id} className="p-4 grid gap-3 md:grid-cols-[1fr_auto] items-center" data-testid={`prosaic-entity-${e.prosaic_entity_id}`}>
                        <div className="min-w-0">
                            <div className="font-medium text-sm truncate">{e.prosaic_entity_name}</div>
                            <div className="text-[11px] text-[#5E636E] truncate">
                                {(e.bank_accounts || []).map((b) => `${b.institution || "?"}·${b.name || "?"}`).join(" · ") || "no bank accounts"}
                            </div>
                            {e.last_synced_at && (
                                <div className="mt-1 text-[11px] text-[#5E636E]">Last sync: {relativeTime(e.last_synced_at)}</div>
                            )}
                            {e.last_sync_errors && e.last_sync_errors.length > 0 && (
                                <div className="mt-1 text-[11px] text-rose-700 truncate" title={e.last_sync_errors.join(" | ")}>Errors: {e.last_sync_errors[0]}</div>
                            )}
                            <div className="mt-2 flex items-center gap-2">
                                <Label className="text-[11px] uppercase tracking-[0.14em] text-[#5E636E] shrink-0">Maps to</Label>
                                <Select
                                    value={e.clientops_client_id || ""}
                                    onValueChange={(v) => mapEntity(e, v)}
                                >
                                    <SelectTrigger className="h-8 w-64" data-testid={`prosaic-map-${e.prosaic_entity_id}`}>
                                        <SelectValue placeholder="Pick a ClientOps client…" />
                                    </SelectTrigger>
                                    <SelectContent className="max-h-72">
                                        {clients.map((cl) => (
                                            <SelectItem key={cl.id} value={cl.id}>{cl.name}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <Button
                                variant="outline"
                                onClick={() => syncNow(e)}
                                disabled={!e.clientops_client_id || busy === e.prosaic_entity_id}
                                data-testid={`prosaic-sync-${e.prosaic_entity_id}`}
                            >
                                {busy === e.prosaic_entity_id ? "Syncing…" : "Sync now"}
                            </Button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

